import { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Sparkles, Loader2 } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { MessageReactions, toggleReaction } from "@/components/MessageReactions";

type SupportMessage = {
  id: string;
  senderId: string;
  recipientId: string;
  body: string;
  isRead: number;
  createdAt: string | null;
  senderName: string | null;
  quickReplies?: string[] | null;
  reactions: Record<string, string> | null;
};

type ThreadResponse = { messages: SupportMessage[]; otherPartyName: string };

type SupportChatProps = {
  // The other party in the thread. For a regular user this is the admin's id;
  // for the admin this is the user they're messaging.
  otherUserId: string;
  otherPartyName: string;
};

function formatTime(dateStr: string | null) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function SupportChat({ otherUserId, otherPartyName }: SupportChatProps) {
  const { user, isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [text, setText] = useState("");
  const [adminFromTitle, setAdminFromTitle] = useState("Stoop Support");
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevCountRef = useRef(0);

  const userId = user?.id;
  const threadKey = ["/api/support-messages/thread", otherUserId];

  const { data, isLoading } = useQuery<ThreadResponse>({
    queryKey: threadKey,
    queryFn: async () => {
      const res = await fetch(`/api/support-messages/thread/${otherUserId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(await res.text());
      return res.json();
    },
    refetchInterval: 15000,
    enabled: !!otherUserId,
  });
  const messages = data?.messages ?? [];
  // Show a read receipt only under the most recent message the viewer sent.
  let lastOwnIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].senderId === userId) {
      lastOwnIndex = i;
      break;
    }
  }
  // Prefer the server-resolved name (always correct) over the passed-in prop,
  // which is null/"User" when the thread was opened from a notification.
  const displayName = data?.otherPartyName || otherPartyName;

  // Fetching the thread marks the counterparty's messages read server-side.
  // Refresh the inbox badges (staleTime: Infinity means we must invalidate).
  useEffect(() => {
    if (!otherUserId || isLoading) return;
    queryClient.invalidateQueries({ queryKey: ["/api/my/conversations"] });
    queryClient.invalidateQueries({ queryKey: ["/api/my/unread-messages"] });
    queryClient.invalidateQueries({ queryKey: ["/api/admin/support-conversations"] });
  }, [otherUserId, isLoading, queryClient]);

  const sendMutation = useMutation({
    mutationFn: async (body: string) => {
      const res = await apiRequest("POST", "/api/support-messages", {
        recipientId: otherUserId,
        body,
        ...(isAdmin ? { title: adminFromTitle } : {}),
      });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: threadKey });
      queryClient.invalidateQueries({ queryKey: ["/api/my/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/support-conversations"] });
    },
  });

  const reactMutation = useMutation({
    mutationFn: async ({ messageId, emoji }: { messageId: string; emoji: string }) => {
      const res = await apiRequest("POST", `/api/support-messages/${messageId}/react`, { emoji });
      return res.json();
    },
    onMutate: async ({ messageId, emoji }) => {
      await queryClient.cancelQueries({ queryKey: threadKey });
      const prev = queryClient.getQueryData<ThreadResponse>(threadKey);
      if (prev && userId) {
        queryClient.setQueryData<ThreadResponse>(threadKey, {
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === messageId
              ? { ...m, reactions: toggleReaction(m.reactions, userId, emoji) }
              : m,
          ),
        });
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(threadKey, ctx.prev);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: threadKey });
    },
  });

  const handleSend = () => {
    const trimmed = text.trim();
    if (!trimmed || sendMutation.isPending) return;
    setText("");
    sendMutation.mutate(trimmed);
  };

  useEffect(() => {
    if (messages.length > prevCountRef.current) {
      scrollRef.current?.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: prevCountRef.current === 0 ? "instant" : "smooth",
      });
    }
    prevCountRef.current = messages.length;
  }, [messages.length]);

  return (
    <div className="flex flex-col rounded-lg border bg-background">
      <div className="flex items-center gap-2 border-b px-4 py-2.5">
        <Sparkles className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold">{displayName}</span>
      </div>

      <div
        ref={scrollRef}
        className="flex flex-col gap-2 overflow-y-auto px-4 py-3"
        style={{ maxHeight: "320px", minHeight: "120px" }}
      >
        {isLoading && (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            Loading messages...
          </div>
        )}

        {!isLoading && messages.length === 0 && (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            No messages yet.
          </div>
        )}

        {messages.map((msg, idx) => {
          const isOwn = msg.senderId === userId;
          return (
            <div
              key={msg.id}
              className={`flex flex-col max-w-[80%] ${isOwn ? "self-end items-end" : "self-start items-start"}`}
            >
              <span className="text-[10px] text-muted-foreground mb-0.5 px-1">
                {isOwn ? "You" : msg.senderName || displayName}
              </span>
              <div
                className={`rounded-xl px-3 py-2 text-sm ${
                  isOwn ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                }`}
              >
                {msg.body}
              </div>
              <div className="mt-0.5 px-1">
                <MessageReactions
                  reactions={msg.reactions}
                  currentUserId={userId}
                  onReact={(emoji) => reactMutation.mutate({ messageId: msg.id, emoji })}
                  align={isOwn ? "end" : "start"}
                />
              </div>
              <span className="text-[10px] text-muted-foreground mt-0.5 px-1">
                {formatTime(msg.createdAt)}
                {idx === lastOwnIndex && (
                  <span className="ml-1">{msg.isRead ? "· Read" : "· Sent"}</span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      {(() => {
        // Tappable quick-replies appear when the latest message (from the
        // other party) carries poll options. Tapping sends it as a reply.
        const last = messages[messages.length - 1];
        const opts = last && last.senderId !== userId ? last.quickReplies ?? [] : [];
        if (!opts.length) return null;
        return (
          <div className="flex flex-wrap gap-2 border-t px-3 py-2.5">
            {opts.map((opt) => (
              <button
                key={opt}
                type="button"
                disabled={sendMutation.isPending}
                onClick={() => sendMutation.mutate(opt)}
                className="rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary transition hover:bg-primary/10 disabled:opacity-50"
              >
                {opt}
              </button>
            ))}
          </div>
        );
      })()}

      {isAdmin && (
        <div className="flex items-center gap-2 border-t px-3 pt-2 text-xs text-muted-foreground">
          <span>Send as:</span>
          <select
            value={adminFromTitle}
            onChange={(e) => setAdminFromTitle(e.target.value)}
            className="rounded-md border bg-background px-2 py-1 text-xs"
          >
            <option value="Stoop Support">Stoop Support</option>
            <option value="Mark, founder of Stoop">Mark (founder)</option>
          </select>
        </div>
      )}

      <div className={`flex items-center gap-2 px-3 py-2.5 ${isAdmin ? "" : "border-t"}`}>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          placeholder="Type a message..."
          className="flex-1 text-sm"
        />
        <Button
          type="button"
          size="icon"
          disabled={!text.trim() || sendMutation.isPending}
          onClick={handleSend}
          aria-label="Send message"
        >
          {sendMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Send className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}
